import React, { useState } from 'react';

import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';

import {
  useCreateTwoFactorDisablementMutation,
  useCreateTwoFactorEnablementMutation,
  useCreateTwoFactorProvisioningMutation,
} from 'components/hooks/twoFactorMutations';
import useModal from 'components/hooks/useModal';
import useOneTimePassword from 'components/hooks/useOneTimePassword';
import useValidationErrors from 'components/hooks/useValidationErrors';

import OutlinedButton from 'components/application/buttons/OutlinedButton';
import SuccessToast from 'components/application/SuccessToast';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useBreadBoard } from 'components/contexts/Toaster';
import DisableModal from 'components/settings/profile/twoFactorAuthentication/DisableModal';
import EnableModal from 'components/settings/profile/twoFactorAuthentication/EnableModal';

export default function TwoFactorAuthenticationSection({ initialEnabledAt }) {
  const breadBoard = useBreadBoard();

  const { account } = useCurrentActor();
  const isTwoFactorEnforced = account.attributes.twoFactorEnforced;

  const [isDisableModalOpen, , openDisableModal, closeDisableModal] = useModal(
    false,
    {},
  );

  const [isEnableModalOpen, , openEnableModal, closeEnableModal] = useModal(
    false,
    {},
  );

  const oneTimePasswordProps = useOneTimePassword();

  const [enabledAt, setEnabledAt] = useState(initialEnabledAt);
  const [provisioningUri, setProvisioningUri] = useState('');

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'settings.profile.TwoFactorAuthenticationSection',
  });

  const { t: i18nTAuthentication } = useTranslation('common', {
    keyPrefix: 'authentication',
  });

  const validationErrorsProps = useValidationErrors({});

  const createProvisioningMutation = useCreateTwoFactorProvisioningMutation();
  const createEnablementMutation = useCreateTwoFactorEnablementMutation();
  const createDisablementMutation = useCreateTwoFactorDisablementMutation();

  const handleReset = () => {
    oneTimePasswordProps.handleCleanupField();
    validationErrorsProps.handleValidationErrorsReset();
  };

  const handleCloseDisableModal = () => {
    handleReset();
    closeDisableModal();
  };

  const handleCloseEnableModal = () => {
    handleReset();
    closeEnableModal();
  };

  const handleDisableModalFormSubmit = (otpCode) => {
    createDisablementMutation.mutate(
      { otpAttempt: otpCode.join('') },
      {
        onSuccess: () => {
          handleCloseDisableModal();
          setEnabledAt(null);

          breadBoard.addToast(
            <SuccessToast
              message={i18nT('handleDisableModalFormSubmit.success')}
            />,
          );
        },
        onError: () => breadBoard.addInedibleToast(),
      },
    );
  };

  const handleEnableModalFormSubmit = (otpCode) => {
    createEnablementMutation.mutate(
      { otpAttempt: otpCode.join('') },
      {
        onSuccess: () => {
          handleCloseEnableModal();
          setEnabledAt(
            new Date().toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            }),
          );

          breadBoard.addToast(
            <SuccessToast
              message={i18nT('handleEnableModalFormSubmit.success')}
            />,
          );
        },
        onError: () => {
          oneTimePasswordProps.handleCleanupField();
          validationErrorsProps.handleValidationErrorsAssignment({
            otp_attempt: [
              i18nTAuthentication('errors.incorrectVerificationCode'),
            ],
          });
        },
      },
    );
  };

  const handleOpenEnableModal = () => {
    // Fetch provisioningUri to render QR code
    createProvisioningMutation.mutate(
      {},
      {
        onSuccess: (data) => {
          setProvisioningUri(data.data.attributes.uri);
          openEnableModal();
        },
        onError: () => breadBoard.addInedibleToast(),
      },
    );
  };

  return (
    <>
      <div>
        <Trans
          components={{
            a: (
              /* eslint-disable jsx-a11y/anchor-has-content */
              <a
                className='app-link tw-text-blue-500 hover:tw-text-blue-300'
                href='https://intercom.help/hands-hq/en/articles/8862590-enable-2-factor-authentication-2fa'
                rel='noreferrer'
                target='_blank'
              />
              /* eslint-enable jsx-a11y/anchor-has-content */
            ),
          }}
          i18nKey={`settings.profile.TwoFactorAuthenticationSection.message.${enabledAt ? 'enabled' : 'disabled'}`}
          ns='components'
          values={{ enabledAt }}
        />
      </div>
      {!isTwoFactorEnforced && (
        <OutlinedButton
          className='tw-mb-5 tw-mt-6'
          color={enabledAt ? 'red' : 'grey'}
          onClick={enabledAt ? openDisableModal : handleOpenEnableModal}
          size='sm'
        >
          {enabledAt ? i18nT('turnOff') : i18nTAuthentication('setUpTwoFactor')}
        </OutlinedButton>
      )}
      {enabledAt ?
        <DisableModal
          closeModal={handleCloseDisableModal}
          isOpen={isDisableModalOpen}
          onFormSubmit={handleDisableModalFormSubmit}
          oneTimePasswordProps={oneTimePasswordProps}
          validationErrorsProps={validationErrorsProps}
        />
      : <EnableModal
          closeModal={handleCloseEnableModal}
          isOpen={isEnableModalOpen}
          onFormSubmit={handleEnableModalFormSubmit}
          oneTimePasswordProps={oneTimePasswordProps}
          provisioningUri={provisioningUri}
          validationErrorsProps={validationErrorsProps}
        />
      }
    </>
  );
}

TwoFactorAuthenticationSection.propTypes = {
  initialEnabledAt: PropTypes.string,
};
